import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Topbar from "../components/layout/Topbar.jsx";
import { useAuth } from "../context/AuthContext.jsx";
import { PriorityBadge, TicketStatusBadge } from "../components/ui/index.jsx";
import {
  adminGetTicket,
  adminUpdateTicket,
  adminDeleteTicket,
  adminGetComments,
  adminAddComment,
  getTechnicians,
  getCompanyAssets,
} from "../services/ticketService.js";
import {
  getContractsForCompany,
  billTicket,
  getTicketBillingStatus,
} from "../services/billingService.js";

// ─────────────────────────────────────────────
//  CONSTANTES
// ─────────────────────────────────────────────

const STATUSES = [
  { value: "OPEN",        label: "Ouvert",     color: "#3B82F6" },
  { value: "IN_PROGRESS", label: "En cours",   color: "#F59E0B" },
  { value: "PENDING",     label: "En attente", color: "#8B5CF6" },
  { value: "RESOLVED",    label: "Résolu",     color: "#10B981" },
  { value: "CLOSED",      label: "Fermé",      color: "#6B7280" },
];

const PRIORITIES = [
  { value: "LOW",      label: "Basse",    color: "#6B7280" },
  { value: "MEDIUM",   label: "Moyenne",  color: "#F59E0B" },
  { value: "HIGH",     label: "Haute",    color: "#EF4444" },
  { value: "CRITICAL", label: "Critique", color: "#7C3AED" },
];

// ── SLA — mêmes seuils que sur la liste des tickets (TicketsPage.jsx) ───────
const SLA_THRESHOLDS = {
  CRITICAL: { warning: 2,  late: 4  },
  HIGH:     { warning: 4,  late: 8  },
  MEDIUM:   { warning: 12, late: 24 },
  LOW:      { warning: 24, late: 48 },
};
const SLA_OPEN_STATUSES = ["OPEN", "IN_PROGRESS", "PENDING"];

function getSlaState(ticket) {
  if (!SLA_OPEN_STATUSES.includes(ticket.status) || !ticket.createdAt) return null;
  const thresholds = SLA_THRESHOLDS[ticket.priority] || SLA_THRESHOLDS.LOW;
  const hoursElapsed = (Date.now() - new Date(ticket.createdAt).getTime()) / 3_600_000;
  if (hoursElapsed >= thresholds.late)    return "late";
  if (hoursElapsed >= thresholds.warning) return "warning";
  return null;
}

/** Badge texte "En retard SLA" / "Proche limite SLA" — pour l'en-tête du ticket. */
function SlaBadge({ ticket }) {
  const state = getSlaState(ticket);
  if (!state) return null;
  const isLate = state === "late";
  return (
    <span style={{
      background: isLate ? "#FEE2E2" : "#FEF3C7",
      color:      isLate ? "#DC2626" : "#B45309",
      borderRadius: 20, padding: "3px 9px", fontSize: 11, fontWeight: 600,
    }}>
      <i className={`bi ${isLate ? "bi-alarm" : "bi-clock-history"} me-1`}/>
      {isLate ? "En retard SLA" : "Proche limite SLA"}
    </span>
  );
}

/**
 * Nettoie le HTML d'Atera pour un affichage lisible — affichage inchangé
 * (un seul bloc de texte, comme avant), seul le nettoyage est renforcé.
 *
 * Ajout du 14/09/2026 : le bloc <style>...</style> n'était retiré que s'il
 * était bien formé — un export Outlook/Word qui laisse ses déclarations
 * CSS hors de toute balise (ex: "P { margin-top: 0; margin-bottom: 0 }")
 * s'affichait sinon en texte brut avant le vrai message. Le filet de
 * sécurité ci-dessous retire ce résidu, quel que soit son origine.
 */
function stripHtml(html) {
  if (!html) return "";

  let text = html
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, " ")
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, "\"")
    .replace(/&#39;|&apos;/g, "'")
    .replace(/&amp;/g, "&");

  // Filet de sécurité : règle(s) CSS isolée(s) qui subsistent en texte
  // brut — bloc <style> mal formé ou déclaration CSS collée sans aucune
  // balise. Motif générique "sélecteur { propriété: valeur; … }".
  text = text.replace(/\b[A-Za-z][\w.#,:>~+\s-]{0,150}\{\s*(?:[\w-]+\s*:\s*[^;{}]{0,300};?\s*){1,30}\}/g, " ");

  return text.replace(/\s{2,}/g, " ").trim();
}

function getInitials(author) {
  if (!author) return "?";
  return `${author.firstName?.[0] ?? ""}${author.lastName?.[0] ?? ""}`.toUpperCase() || "?";
}

function getFullName(author) {
  if (!author) return "Inconnu";
  return `${author.firstName ?? ""} ${author.lastName ?? ""}`.trim() || "Inconnu";
}

/**
 * Carte "Machine concernée" — contexte machine automatique (ajouté le
 * 25/09/2026). Affiche l'état Atera de l'actif lié au ticket (en ligne/hors
 * ligne, IP, OS, utilisateur connecté, dernier reboot, occupation disque)
 * pour que le technicien n'ait pas à aller chercher cette information
 * ailleurs dans Deskora.
 *
 * ⚠️ Le statut "en ligne" suppose que AssetStatus utilise la valeur
 * littérale "ONLINE" (cohérent avec le reste du champ status d'Asset) — à
 * ajuster si votre enum utilise d'autres libellés, auquel cas la valeur
 * brute s'affiche simplement telle quelle sans le point vert.
 */
function AssetContextCard({ ticket, assets, savingAsset, isLocked, onAssign }) {
  const asset = ticket.asset;
  const online = asset?.status === "ONLINE";

  const details = asset ? [
    ["IP",                        asset.ipAddress],
    ["OS",                        asset.os],
    ["Utilisateur connecté",      asset.currentLoggedUser],
    ["Dernier reboot",            asset.lastRebootTime ? new Date(asset.lastRebootTime).toLocaleString("fr-FR") : null],
    ["Dernière connexion agent",  asset.lastSeenAt      ? new Date(asset.lastSeenAt).toLocaleString("fr-FR")      : null],
  ].filter(([, v]) => v) : [];

  const diskPct = asset?.diskTotalGb && asset?.diskUsedGb != null
    ? Math.min(100, Math.round((asset.diskUsedGb / asset.diskTotalGb) * 100))
    : null;
  const diskColor = diskPct == null ? null : diskPct >= 85 ? "#EF4444" : diskPct >= 70 ? "#F59E0B" : "#10B981";

  return (
    <div className="itm-card anim-fade-up">
      <div className="card-title" style={{ marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
        Machine concernée
        {savingAsset && <i className="bi bi-arrow-repeat" style={{ fontSize: 12, color: "var(--text-3)", animation: "spin .7s linear infinite" }}/>}
      </div>

      <select className="itm-select" style={{ width: "100%", marginBottom: asset ? 12 : 0 }}
        value={asset?.id || ""}
        disabled={savingAsset || isLocked}
        onChange={e => onAssign(e.target.value)}>
        <option value="">— Aucune machine —</option>
        {assets.map(a => (
          <option key={a.id} value={a.id}>{a.hostname || a.serialNumber || a.id}</option>
        ))}
        {/* Si la machine actuellement liée ne fait plus partie de la liste
            reçue (ex: supprimée depuis l'inventaire), on l'affiche quand
            même pour ne pas perdre l'information silencieusement — même
            principe que le technicien introuvable ci-dessous. */}
        {asset && !assets.some(a => a.id === asset.id) && (
          <option value={asset.id}>{asset.hostname || asset.id} (introuvable)</option>
        )}
      </select>

      {asset && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, fontSize: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: online ? "#10B981" : "#9CA3AF", display: "inline-block", flexShrink: 0 }}/>
            <span style={{ color: online ? "#10B981" : "var(--text-3)", fontWeight: 600 }}>
              {online ? "En ligne" : (asset.status || "Statut inconnu")}
            </span>
          </div>

          {details.map(([k, v]) => (
            <div key={k} style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
              <span style={{ color: "var(--text-3)" }}>{k}</span>
              <span style={{ color: "var(--text-2)", textAlign: "right" }}>{v}</span>
            </div>
          ))}

          {diskPct != null && (
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                <span style={{ color: "var(--text-3)" }}>Disque</span>
                <span style={{ color: diskColor, fontWeight: 600 }}>
                  {asset.diskUsedGb} / {asset.diskTotalGb} Go ({diskPct}%)
                </span>
              </div>
              <div style={{ height: 6, borderRadius: 4, background: "var(--navy-soft)", overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${diskPct}%`, background: diskColor, transition: "width .3s" }}/>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Carte "Facturation" — bouton "Facturer ce ticket" en un clic (ajouté le
 * 28/09/2026). Crée une ligne de consommation sur le contrat choisi de
 * l'entreprise du ticket (voir BillingController#billTicket). Un ticket ne
 * peut être facturé qu'une seule fois — la vraie protection est côté
 * serveur (BillingHourEntryRepository#findByTicketId) ; cette carte ne fait
 * que refléter l'état renvoyé par le backend, elle ne l'impose pas.
 *
 * contracts et billingEntry sont chargés par le composant parent (même
 * principe que assets/AssetContextCard) — cette carte ne gère que son
 * propre formulaire local.
 */
function BillingCard({ contracts, billingEntry, onBill }) {
  const [showForm,    setShowForm]    = useState(false);
  const [contractId,  setContractId]  = useState("");
  const [hours,        setHours]      = useState("1");
  const [description,  setDescription] = useState("");
  const [submitting,  setSubmitting]  = useState(false);
  const [error,       setError]       = useState("");

  const openForm = () => {
    setError("");
    const def = contracts.find(r => r.contract.isDefault);
    setContractId(def ? def.contract.id : (contracts[0]?.contract.id || ""));
    setHours("1");
    setDescription("");
    setShowForm(true);
  };

  const handleSubmit = async () => {
    setError("");
    const h = parseFloat(hours);
    if (!contractId) return setError("Choisissez un contrat.");
    if (!h || h <= 0) return setError("Saisissez un nombre d'heures valide.");

    setSubmitting(true);
    try {
      await onBill({ contractId, hours: h, description: description.trim() || undefined });
      setShowForm(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="itm-card anim-fade-up">
      <div className="card-title" style={{ marginBottom: 12 }}>Facturation</div>

      {billingEntry === undefined && (
        <div style={{ fontSize: 12, color: "var(--text-3)" }}>Vérification…</div>
      )}

      {billingEntry === null && !showForm && (
        contracts.length > 0 ? (
          <button className="btn-itm btn-itm-primary" style={{ width: "100%", justifyContent: "center" }}
            onClick={openForm}>
            <i className="bi bi-receipt me-1"/>Facturer ce ticket
          </button>
        ) : (
          <div style={{ fontSize: 12, color: "var(--text-3)" }}>
            Aucun contrat de facturation pour cette entreprise.
          </div>
        )
      )}

      {billingEntry === null && showForm && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {error && <div style={{ fontSize: 11, color: "var(--danger)" }}>{error}</div>}

          <select className="itm-select" style={{ width: "100%" }}
            value={contractId} onChange={e => setContractId(e.target.value)}>
            {contracts.map(r => (
              <option key={r.contract.id} value={r.contract.id}>
                {r.contract.name}{r.hoursRemaining !== "" ? ` (${r.hoursRemaining} h restantes)` : ""}
              </option>
            ))}
          </select>

          <input className="itm-input" type="number" min="0" step="0.25"
            placeholder="Heures travaillées" value={hours}
            onChange={e => setHours(e.target.value)}/>

          <input className="itm-input" type="text"
            placeholder="Description (optionnel)"
            value={description} onChange={e => setDescription(e.target.value)}/>

          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn-itm btn-itm-outline" style={{ flex: 1 }} onClick={() => setShowForm(false)}>
              Annuler
            </button>
            <button className="btn-itm btn-itm-primary" style={{ flex: 1, justifyContent: "center" }}
              onClick={handleSubmit} disabled={submitting}>
              {submitting ? "…" : "Confirmer"}
            </button>
          </div>
        </div>
      )}

      {billingEntry && (
        <div style={{ fontSize: 12, color: "var(--text-2)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6, color: "#10B981", fontWeight: 600 }}>
            <i className="bi bi-check-circle-fill"/>Facturé
          </div>
          <div>{Math.abs(Number(billingEntry.hours))} h — {billingEntry.contract?.name}</div>
          {billingEntry.createdAt && (
            <div style={{ color: "var(--text-3)" }}>{new Date(billingEntry.createdAt).toLocaleDateString("fr-FR")}</div>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
//  PAGE PRINCIPALE
// ─────────────────────────────────────────────

export default function TicketDetailPage() {
  const { id }    = useParams();
  const navigate  = useNavigate();
  const { user }  = useAuth(); // user.company = entreprise CPUC de l'admin connecté

  // ── Données ──────────────────────────────────
  const [ticket,   setTicket]   = useState(null);
  const [comments, setComments] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);

  // ── Onglet actif ─────────────────────────────
  const [tab, setTab] = useState("conversation");

  // ── Commentaire ──────────────────────────────
  const [newComment,  setNewComment]  = useState("");
  const [sendingCmnt, setSendingCmnt] = useState(false);
  // Note interne (CDC 4.4.2) — jamais visible côté client, voir adminAddComment.
  const [isInternal,  setIsInternal]  = useState(false);

  // ── Modification en ligne ────────────────────
  const [saving, setSaving] = useState(false);

  // ── Affectation technicien ────────────────────
  // Liste déroulante des utilisateurs de CPUC (l'entreprise de l'admin
  // connecté, pas celle du client) — voir getTechnicians (ticketService.js).
  // Le champ reste une chaîne libre côté backend (assignedTechnicianName,
  // pas de relation vers User) : on y stocke simplement le nom complet du
  // technicien choisi. Modifié le 14/09/2026 : avant, un champ texte libre
  // laissait taper n'importe quoi et le bouton ✓ à côté servait à valider
  // la saisie — remplacé par une sélection directe qui enregistre aussitôt.
  const [technicians, setTechnicians] = useState([]);
  const [savingTech,  setSavingTech]  = useState(false);

  // ── Machine concernée (contexte machine automatique) ─────────────────
  // Liste des machines de l'entreprise DU TICKET (pas celle de l'admin —
  // voir getCompanyAssets, différent de getTechnicians ci-dessus).
  // Ajouté le 25/09/2026.
  const [assets,      setAssets]      = useState([]);
  const [savingAsset, setSavingAsset] = useState(false);

  // ── Facturation (bouton "Facturer ce ticket") ─────────────────────────
  // Contrats actifs de l'entreprise DU TICKET (même source que la machine
  // concernée ci-dessus) et statut de facturation de CE ticket précis.
  // billingEntry : undefined = en cours de vérification, null = pas encore
  // facturé, objet = déjà facturé (voir BillingController#billTicket).
  // Ajouté le 28/09/2026.
  const [billingContracts, setBillingContracts] = useState([]);
  const [billingEntry,     setBillingEntry]     = useState(undefined);

  // Corrigé le 15/09/2026 : certains liens (souvent construits à partir d'un
  // objet ticket incomplet ailleurs dans l'app, ex. `t.id` absent d'une
  // réponse condensée) pointaient vers /tickets/undefined — le id de l'URL
  // était alors littéralement la chaîne "undefined", ce qui déclenchait des
  // requêtes en boucle vers /api/tickets/admin/undefined (rejetées côté
  // backend : "Invalid UUID string: undefined"). On détecte ce cas avant
  // même d'appeler l'API pour éviter le bruit réseau et afficher un message
  // clair plutôt qu'une erreur technique.
  const hasValidId = !!id && id !== "undefined" && id !== "null";

  // Chargement initial — ticket + commentaires
  useEffect(() => { fetchAll(); }, [id]);

  const fetchAll = async () => {
    if (!hasValidId) {
      setError("Lien de ticket invalide — impossible de déterminer quel ticket ouvrir.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [ticketRes, commentsRes] = await Promise.all([
        adminGetTicket(id),
        adminGetComments(id),
      ]);
      setTicket(ticketRes.data ?? ticketRes);
      setComments(Array.isArray(commentsRes) ? commentsRes : commentsRes.data ?? []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Liste des techniciens — dépend de l'entreprise CPUC de l'admin connecté
  // (voir useAuth), chargée séparément car user peut arriver après le
  // premier rendu.
  useEffect(() => {
    if (!user?.company?.id) return;
    getTechnicians(user.company.id)
      .then(res => setTechnicians(Array.isArray(res) ? res : res.data ?? []))
      .catch(() => setTechnicians([]));
  }, [user?.company?.id]);

  // Liste des machines — dépend de l'entreprise DU TICKET (le client), pas
  // de celle de l'admin connecté. Chargée séparément dès que le ticket est
  // disponible. Ajouté le 25/09/2026.
  useEffect(() => {
    if (!ticket?.company?.id) return;
    getCompanyAssets(ticket.company.id)
      .then(res => setAssets(Array.isArray(res) ? res : res.data ?? []))
      .catch(() => setAssets([]));
  }, [ticket?.company?.id]);

  // Contrats de facturation de l'entreprise DU TICKET — même timing que les
  // machines ci-dessus. Ajouté le 28/09/2026.
  useEffect(() => {
    if (!ticket?.company?.id) return;
    getContractsForCompany(ticket.company.id)
      .then(res => setBillingContracts(res.data || []))
      .catch(() => setBillingContracts([]));
  }, [ticket?.company?.id]);

  // Statut de facturation de ce ticket précis. Ajouté le 28/09/2026.
  useEffect(() => {
    if (!ticket?.id) return;
    getTicketBillingStatus(ticket.id)
      .then(res => setBillingEntry(res.data ?? null))
      .catch(() => setBillingEntry(null));
  }, [ticket?.id]);

  // Modification du statut ou de la priorité
  const handleUpdate = async (field, value) => {
    setSaving(true);
    try {
      const res = await adminUpdateTicket(id, { [field]: value });
      setTicket(res.data ?? res);
    } catch (err) {
      alert("Erreur : " + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Affectation du technicien — enregistre immédiatement à la sélection
  // dans la liste déroulante (plus de bouton de validation séparé).
  const handleAssignTechnician = async (name) => {
    setSavingTech(true);
    try {
      const res = await adminUpdateTicket(id, { assignedTechnicianName: name });
      setTicket(res.data ?? res);
    } catch (err) {
      alert("Erreur : " + err.message);
    } finally {
      setSavingTech(false);
    }
  };

  // Rattachement de la machine concernée — enregistre immédiatement à la
  // sélection, même principe que handleAssignTechnician. Une valeur vide
  // délie la machine (voir TicketController#adminUpdateTicket).
  // Ajouté le 25/09/2026.
  const handleAssignAsset = async (assetId) => {
    setSavingAsset(true);
    try {
      const res = await adminUpdateTicket(id, { assetId });
      setTicket(res.data ?? res);
    } catch (err) {
      alert("Erreur : " + err.message);
    } finally {
      setSavingAsset(false);
    }
  };

  // Facture le ticket en un clic — appelé par BillingCard. Les erreurs sont
  // remontées à l'appelant (qui les affiche dans son propre formulaire)
  // plutôt qu'interceptées ici, contrairement aux autres handlers de cette
  // page. Ajouté le 28/09/2026.
  const handleBillTicket = async ({ contractId, hours, description }) => {
    const res = await billTicket(id, { contractId, hours, description });
    setBillingEntry(res.data ?? res);
  };

  // Envoi d'un commentaire — public ou note interne selon isInternal
  const handleSendComment = async () => {
    if (!newComment.trim()) return;
    setSendingCmnt(true);
    try {
      const res = await adminAddComment(id, { content: newComment, internal: isInternal ? "true" : "false" });
      setComments(prev => [...prev, res.data ?? res]);
      setNewComment("");
      setIsInternal(false);
      // Met à jour updatedAt affiché
      setTicket(prev => ({ ...prev, updatedAt: new Date().toISOString() }));
    } catch (err) {
      alert("Erreur : " + err.message);
    } finally {
      setSendingCmnt(false);
    }
  };

  // Suppression du ticket
  const handleDelete = async () => {
    if (!window.confirm("Supprimer ce ticket définitivement ?")) return;
    try {
      await adminDeleteTicket(id);
      navigate("/tickets");
    } catch (err) {
      alert("Erreur : " + err.message);
    }
  };

  // ── États intermédiaires ─────────────────────
  if (loading) return (
    <>
      <Topbar title="Tickets" subtitle="détail"/>
      <div className="page-body" style={{ textAlign:"center", padding:60, color:"var(--text-3)" }}>
        <i className="bi bi-arrow-repeat" style={{ fontSize:24, display:"block", marginBottom:8 }}/>
        Chargement du ticket…
      </div>
    </>
  );

  if (error || !ticket) return (
    <>
      <Topbar title="Tickets" subtitle="détail"/>
      <div className="page-body">
        <div style={{ background:"var(--danger-dim)", border:"1px solid var(--danger)", borderRadius:10, padding:14, color:"var(--danger)", display:"flex", alignItems:"center", gap:12, flexWrap:"wrap" }}>
          <i className="bi bi-exclamation-triangle"/>{error || "Ticket introuvable."}
          {hasValidId ? (
            <button className="btn-itm btn-itm-outline" style={{ marginLeft:"auto" }} onClick={fetchAll}>Réessayer</button>
          ) : (
            <button className="btn-itm btn-itm-outline" style={{ marginLeft:"auto" }} onClick={() => navigate("/tickets")}>
              <i className="bi bi-arrow-left me-1"/>Retour à la liste
            </button>
          )}
        </div>
      </div>
    </>
  );

  // ── Verrouillage ticket résolu / fermé ────────
  // Un ticket FERMÉ est en lecture seule totale. Un ticket RÉSOLU ne peut
  // plus être modifié SAUF le statut (pour le rouvrir ou le fermer) — voir
  // TicketController#adminUpdateTicket côté backend, qui applique la même
  // règle (le frontend n'est qu'un confort, la sécurité est côté serveur).
  const isClosed = ticket.status === "CLOSED";
  const isLocked = isClosed || ticket.status === "RESOLVED";

  return (
    <>
      <Topbar title="Tickets" subtitle="détail"/>
      <div className="page-body">

        {/* Breadcrumb */}
        <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:18, fontSize:13, color:"var(--text-3)" }}>
          <span style={{ cursor:"pointer", color:"var(--accent)" }} onClick={() => navigate("/tickets")}>
            <i className="bi bi-arrow-left me-1"/>Tickets
          </span>
          <i className="bi bi-chevron-right" style={{ fontSize:10 }}/>
          <span style={{ color:"var(--text-2)" }}>
            {ticket.ateraTicketId ? `#${ticket.ateraTicketId}` : ticket.title}
          </span>
          {ticket.company && (
            <>
              <i className="bi bi-chevron-right" style={{ fontSize:10 }}/>
              <span style={{ color:"var(--accent)" }}>{ticket.company.name}</span>
            </>
          )}
        </div>

        <div style={{ display:"grid", gridTemplateColumns:"1fr 300px", gap:20, alignItems:"start" }}>

          {/* ── Colonne principale ── */}
          <div>
            {/* En-tête du ticket */}
            <div className="itm-card anim-fade-up" style={{ marginBottom:16 }}>
              <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:12, marginBottom:14 }}>
                <h2 style={{ fontFamily:"Syne,sans-serif", fontSize:18, fontWeight:800, color:"var(--text-1)", margin:0 }}>
                  {ticket.title}
                </h2>
                {/* Bouton supprimer */}
                <button className="btn-itm btn-itm-outline"
                  onClick={handleDelete}
                  style={{ color:"var(--danger)", borderColor:"rgba(239,68,68,.3)", flexShrink:0 }}>
                  <i className="bi bi-trash me-1"/>Supprimer
                </button>
              </div>

              <div style={{ display:"flex", flexWrap:"wrap", gap:8, marginBottom: ticket.description ? 14 : 0 }}>
                <PriorityBadge priority={ticket.priority}/>
                <TicketStatusBadge status={ticket.status}/>
                <SlaBadge ticket={ticket}/>
                {ticket.category && (
                  <span style={{ background:"var(--navy-soft)", borderRadius:20, padding:"3px 9px", fontSize:11, color:"var(--text-2)" }}>
                    <i className="bi bi-folder me-1"/>{ticket.category}
                  </span>
                )}
                {ticket.company && (
                  <span style={{ background:"var(--accent-dim)", borderRadius:20, padding:"3px 9px", fontSize:11, color:"var(--accent)" }}>
                    <i className="bi bi-building me-1"/>{ticket.company.name}
                  </span>
                )}
                {ticket.asset && (
                  <span style={{ background:"var(--navy-soft)", borderRadius:20, padding:"3px 9px", fontSize:11, color:"var(--text-2)" }}>
                    <i className="bi bi-pc-display me-1"/>{ticket.asset.hostname}
                  </span>
                )}
                {saving && <i className="bi bi-arrow-repeat" style={{ fontSize:14, color:"var(--text-3)", animation:"spin .7s linear infinite" }}/>}
              </div>

              {ticket.description && (
                <p style={{ fontSize:13, color:"var(--text-2)", lineHeight:1.7, padding:12, background:"var(--navy-soft)", borderRadius:10, whiteSpace:"pre-wrap", margin:0 }}>
                  {stripHtml(ticket.description)}
                </p>
              )}

              {isLocked && (
                <div style={{
                  marginTop:14, padding:"9px 12px", borderRadius:10, fontSize:12,
                  background: isClosed ? "var(--navy-soft)" : "#F0FDF4",
                  border: isClosed ? "1px solid var(--border)" : "1px solid #86EFAC",
                  color: isClosed ? "var(--text-3)" : "#15803D",
                }}>
                  <i className="bi bi-lock-fill me-2"/>
                  {isClosed
                    ? "Ticket fermé — lecture seule, aucune modification possible."
                    : "Ticket résolu — verrouillé (statut, priorité, technicien et commentaires). Seul un changement de statut reste possible pour rouvrir ou fermer le ticket."}
                </div>
              )}
            </div>

            {/* Onglets */}
            <div className="itm-tabs">
              {[
                { id:"conversation", label:"Conversation", count:comments.length },
              ].map(t => (
                <div key={t.id} className={`itm-tab ${tab===t.id ? "active" : ""}`} onClick={() => setTab(t.id)}>
                  {t.label}
                  <span style={{ marginLeft:6, background:"var(--navy-soft)", borderRadius:20, padding:"1px 7px", fontSize:11, color:"var(--text-2)" }}>
                    {t.count}
                  </span>
                </div>
              ))}
            </div>

            {/* Conversation */}
            {tab === "conversation" && (
              <div className="anim-fade-in">
                <div style={{ display:"flex", flexDirection:"column", gap:14, marginBottom:16 }}>
                  {comments.length === 0 && (
                    <div style={{ textAlign:"center", padding:30, color:"var(--text-3)", fontSize:13 }}>
                      Aucun commentaire pour le moment.
                    </div>
                  )}
                  {comments.map(c => (
                    <div key={c.id} style={{ display:"flex", gap:12 }}>
                      <div style={{
                        width:34, height:34, borderRadius:"50%", flexShrink:0,
                        background: c.internal ? "linear-gradient(135deg,#F59E0B,#B45309)" : "linear-gradient(135deg,var(--accent3),var(--accent))",
                        display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, fontWeight:700, color:"#fff" }}>
                        {c.internal ? <i className="bi bi-lock-fill"/> : getInitials(c.author)}
                      </div>
                      <div style={{ flex:1 }}>
                        <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
                          <span style={{ fontWeight:600, fontSize:13, color:"var(--text-1)" }}>
                            {getFullName(c.author)}
                          </span>
                          {c.internal && (
                            <span style={{ background:"#FEF3C7", color:"#B45309", borderRadius:20, padding:"1px 8px", fontSize:10, fontWeight:700 }}>
                              NOTE INTERNE
                            </span>
                          )}
                          <span style={{ fontSize:11, color:"var(--text-3)", marginLeft:"auto" }}>
                            {c.createdAt ? new Date(c.createdAt).toLocaleString("fr-FR") : ""}
                          </span>
                        </div>
                        <div style={{
                          background: c.internal ? "#FFFBEB" : "var(--navy-soft)",
                          border: c.internal ? "1px dashed #F59E0B" : "1px solid var(--border)",
                          borderRadius:12, padding:"10px 14px", fontSize:13, color:"var(--text-2)", lineHeight:1.7 }}>
                          {c.content}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Zone de réponse — verrouillée dès que Résolu ou Fermé */}
                {isLocked ? (
                  <div className="itm-card" style={{ padding:14, textAlign:"center", color:"var(--text-3)", fontSize:12 }}>
                    <i className="bi bi-lock-fill me-2"/>
                    {isClosed ? "Ticket fermé — plus aucun commentaire possible." : "Ticket résolu — plus aucun commentaire possible."}
                  </div>
                ) : (
                  <div className="itm-card" style={{ padding:14 }}>
                    <textarea className="itm-input" rows={3}
                      placeholder={isInternal ? "Note interne — jamais visible par le client…" : "Votre réponse…"}
                      value={newComment}
                      onChange={e => setNewComment(e.target.value)}
                      style={{ resize:"vertical", marginBottom:10 }}/>
                    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                      <label style={{ display:"flex", alignItems:"center", gap:6, fontSize:12, color:"var(--text-2)", cursor:"pointer" }}>
                        <input type="checkbox" checked={isInternal} onChange={e => setIsInternal(e.target.checked)}/>
                        <i className="bi bi-lock-fill" style={{ color: isInternal ? "#B45309" : "var(--text-3)" }}/>
                        Note interne (non visible par le client)
                      </label>
                      <button className="btn-itm btn-itm-primary"
                        onClick={handleSendComment}
                        disabled={!newComment.trim() || sendingCmnt}
                        style={isInternal ? { background:"#F59E0B", borderColor:"#F59E0B" } : undefined}>
                        <i className={`bi ${isInternal ? "bi-lock-fill" : "bi-send"}`}/> {sendingCmnt ? "Envoi…" : isInternal ? "Ajouter la note" : "Envoyer"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── Colonne latérale ── */}
          <div style={{ display:"flex", flexDirection:"column", gap:14 }}>

            {/* Modifier statut — reste actif même si Résolu, pour pouvoir
                rouvrir ou fermer le ticket ; verrouillé uniquement si Fermé. */}
            <div className="itm-card anim-fade-up">
              <div className="card-title" style={{ marginBottom:12 }}>Statut</div>
              <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                {STATUSES.map(s => (
                  <button key={s.value}
                    onClick={() => !isClosed && handleUpdate("status", s.value)}
                    disabled={isClosed}
                    style={{
                      padding:"8px 12px", borderRadius:8, border:"1px solid",
                      borderColor: ticket.status===s.value ? s.color : "var(--border)",
                      background:  ticket.status===s.value ? s.color + "20" : "transparent",
                      color:       ticket.status===s.value ? s.color : "var(--text-2)",
                      fontSize:12, cursor: isClosed ? "not-allowed" : "pointer", textAlign:"left", fontWeight: ticket.status===s.value ? 600 : 400,
                      opacity: isClosed ? .6 : 1,
                      transition:"all .15s",
                    }}>
                    {ticket.status===s.value && <i className="bi bi-check2 me-2"/>}
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Modifier priorité — verrouillé dès que Résolu ou Fermé */}
            <div className="itm-card anim-fade-up delay-1">
              <div className="card-title" style={{ marginBottom:12 }}>Priorité</div>
              <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                {PRIORITIES.map(p => (
                  <button key={p.value}
                    onClick={() => !isLocked && handleUpdate("priority", p.value)}
                    disabled={isLocked}
                    style={{
                      padding:"8px 12px", borderRadius:8, border:"1px solid",
                      borderColor: ticket.priority===p.value ? p.color : "var(--border)",
                      background:  ticket.priority===p.value ? p.color + "20" : "transparent",
                      color:       ticket.priority===p.value ? p.color : "var(--text-2)",
                      fontSize:12, cursor: isLocked ? "not-allowed" : "pointer", textAlign:"left", fontWeight: ticket.priority===p.value ? 600 : 400,
                      opacity: isLocked ? .6 : 1,
                      transition:"all .15s",
                    }}>
                    {ticket.priority===p.value && <i className="bi bi-check2 me-2"/>}
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Affectation technicien — CDC 4.4.2 — verrouillé dès que
                Résolu ou Fermé */}
            <div className="itm-card anim-fade-up">
              <div className="card-title" style={{ marginBottom:12, display:"flex", alignItems:"center", gap:8 }}>
                Technicien assigné
                {savingTech && <i className="bi bi-arrow-repeat" style={{ fontSize:12, color:"var(--text-3)", animation:"spin .7s linear infinite" }}/>}
              </div>
              <select className="itm-select" style={{ width:"100%" }}
                value={ticket.assignedTechnicianName || ""}
                disabled={savingTech || isLocked}
                onChange={e => handleAssignTechnician(e.target.value)}>
                <option value="">— Non assigné —</option>
                {technicians.map(t => {
                  const name = `${t.firstName ?? ""} ${t.lastName ?? ""}`.trim() || t.email;
                  return <option key={t.id} value={name}>{name}</option>;
                })}
                {/* Si le technicien actuellement assigné ne fait plus partie
                    de la liste CPUC (ex: compte supprimé depuis), on l'affiche
                    quand même pour ne pas perdre l'information silencieusement. */}
                {ticket.assignedTechnicianName &&
                  !technicians.some(t => `${t.firstName ?? ""} ${t.lastName ?? ""}`.trim() === ticket.assignedTechnicianName) && (
                    <option value={ticket.assignedTechnicianName}>{ticket.assignedTechnicianName} (introuvable)</option>
                  )}
              </select>
            </div>

            {/* Machine concernée — contexte machine automatique.
                Ajouté le 25/09/2026. */}
            <AssetContextCard
              ticket={ticket}
              assets={assets}
              savingAsset={savingAsset}
              isLocked={isLocked}
              onAssign={handleAssignAsset}
            />

            {/* Facturation — bouton "Facturer ce ticket" en un clic.
                Ajouté le 28/09/2026. */}
            <BillingCard
              contracts={billingContracts}
              billingEntry={billingEntry}
              onBill={handleBillTicket}
            />

            {/* Informations */}
            <div className="itm-card anim-fade-up delay-2">
              <div className="card-title" style={{ marginBottom:12 }}>Informations</div>
              {[
                ["Entreprise",   ticket.company?.name ?? "—"],
                ["Catégorie",    ticket.category ?? "—"],
                ["Créé par",     getFullName(ticket.createdBy)],
                ["Créé le",      ticket.createdAt ? new Date(ticket.createdAt).toLocaleDateString("fr-FR") : "—"],
                ["Mis à jour",   ticket.updatedAt ? new Date(ticket.updatedAt).toLocaleDateString("fr-FR") : "—"],
              ].map(([k, v]) => (
                <div key={k} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"7px 0", borderBottom:"1px solid var(--border)", fontSize:12 }}>
                  <span style={{ color:"var(--text-3)" }}>{k}</span>
                  <span style={{ color:"var(--text-2)", textAlign:"right", maxWidth:"60%" }}>{v}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </>
  );
}